/**
 * Rappels locaux (PDF §14, §26) — natif uniquement (@capacitor/local-notifications).
 * Sur le web, toutes les fonctions sont des no-op : l'écran « Rappels » explique que
 * les rappels existent dans l'app mobile.
 *
 * Identifiants stables : 1 = rappel quotidien (heure choisie), 2 = série en jeu (20 h),
 * 3 = révisions dues (8 h). Préférences : users/$uid/profile/notifications
 * ({ enabled, hour, minute }, défaut désactivé). Aucune donnée nominative dans les textes.
 */
import { Capacitor } from "@capacitor/core";
import type { LocalNotificationSchema, LocalNotificationsPlugin } from "@capacitor/local-notifications";
import i18n from "../i18n";
import { dayKey } from "../utils/dates";
import type { Catalog } from "../content/types";
import { NotificationPrefs, ProgressMap, StudentProfile, StudentStats, isParentProfile, saveProfile } from "./progressService";
import { countReviewsDueAt } from "./reviewService";
import { track } from "./analytics";

export const NOTIFICATION_IDS = { daily: 1, streak: 2, reviews: 3 } as const;
export const STREAK_REMINDER_HOUR = 20;
export const REVIEW_REMINDER_HOUR = 8;

export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = { enabled: false, hour: 17, minute: 0 };

export function notificationsAvailable(): boolean {
  return Capacitor.isNativePlatform();
}

export function notificationPrefs(profile: StudentProfile | null | undefined): NotificationPrefs {
  return { ...DEFAULT_NOTIFICATION_PREFS, ...(profile?.notifications || {}) };
}

async function plugin(): Promise<LocalNotificationsPlugin | null> {
  if (!notificationsAvailable()) return null;
  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    return LocalNotifications;
  } catch (e) {
    return null;
  }
}

/** Demande la permission système. false sur le web ou si refusée. */
export async function requestPermission(): Promise<boolean> {
  const ln = await plugin();
  if (!ln) return false;
  try {
    const current = await ln.checkPermissions();
    if (current.display === "granted") return true;
    const res = await ln.requestPermissions();
    return res.display === "granted";
  } catch (e) {
    return false;
  }
}

export async function hasPermission(): Promise<boolean> {
  const ln = await plugin();
  if (!ln) return false;
  try {
    return (await ln.checkPermissions()).display === "granted";
  } catch (e) {
    return false;
  }
}

async function cancelIds(ids: number[]): Promise<void> {
  const ln = await plugin();
  if (!ln) return;
  try {
    await ln.cancel({ notifications: ids.map((id) => ({ id })) });
  } catch (e) {
    /* rien à annuler ou plugin indisponible */
  }
}

async function scheduleOne(n: LocalNotificationSchema): Promise<void> {
  const ln = await plugin();
  if (!ln) return;
  try {
    await ln.cancel({ notifications: [{ id: n.id }] });
    await ln.schedule({ notifications: [n] });
  } catch (e) {
    /* les rappels ne doivent jamais casser l'app */
  }
}

/** Prochaine occurrence (aujourd'hui si encore à venir, sinon demain) d'une heure locale. */
export function nextOccurrence(hour: number, minute: number, from = new Date()): Date {
  const d = new Date(from);
  d.setHours(hour, minute, 0, 0);
  if (d.getTime() <= from.getTime()) d.setDate(d.getDate() + 1);
  return d;
}

/** Rappel quotidien répété à l'heure choisie : « Ton objectif du jour t'attend ». */
export async function scheduleDailyReminder(hour: number, minute: number): Promise<void> {
  await scheduleOne({
    id: NOTIFICATION_IDS.daily,
    title: i18n.t("notifications.daily.title"),
    body: i18n.t("notifications.daily.body"),
    schedule: { on: { hour, minute }, allowWhileIdle: true },
    extra: { kind: "daily" },
  });
}

/**
 * Rappel de série à 20 h, uniquement si la série est en cours et qu'aucune activité n'a eu lieu
 * aujourd'hui. Planifié pour aujourd'hui seulement (pas de répétition) : replanifié à chaque
 * ouverture / activité, et annulé dès que l'élève a travaillé.
 */
export async function scheduleStreakReminder(stats: StudentStats, now = new Date()): Promise<void> {
  const streak = stats.streak?.current || 0;
  const activeToday = stats.streak?.lastActiveDay === dayKey(now);
  const at = new Date(now);
  at.setHours(STREAK_REMINDER_HOUR, 0, 0, 0);
  if (streak <= 0 || activeToday || at.getTime() <= now.getTime()) {
    await cancelIds([NOTIFICATION_IDS.streak]);
    return;
  }
  await scheduleOne({
    id: NOTIFICATION_IDS.streak,
    title: i18n.t("notifications.streak.title"),
    body: i18n.t("notifications.streak.body", { count: streak }),
    schedule: { at, allowWhileIdle: true },
    extra: { kind: "streak" },
  });
}

/** Rappel du matin (8 h) si des révisions sont dues ; count = 0 annule. */
export async function scheduleReviewReminder(count: number, now = new Date()): Promise<void> {
  if (count <= 0) {
    await cancelIds([NOTIFICATION_IDS.reviews]);
    return;
  }
  await scheduleOne({
    id: NOTIFICATION_IDS.reviews,
    title: i18n.t("notifications.reviews.title"),
    body: i18n.t("notifications.reviews.body", { count }),
    schedule: { at: nextOccurrence(REVIEW_REMINDER_HOUR, 0, now), allowWhileIdle: true },
    extra: { kind: "reviews" },
  });
}

export async function cancelAll(): Promise<void> {
  await cancelIds(Object.values(NOTIFICATION_IDS));
}

/** Enregistre les préférences (profil) et trace l'activation (sans donnée nominative). */
export async function saveNotificationPrefs(uid: string, prefs: NotificationPrefs, previous?: NotificationPrefs): Promise<void> {
  await saveProfile(uid, { notifications: prefs });
  if (!previous || previous.enabled !== prefs.enabled) track("notifications_enabled", { enabled: prefs.enabled });
}

export interface SyncInput {
  profile: StudentProfile | null;
  stats: StudentStats;
  progress: ProgressMap;
  catalog: Catalog | null;
}

/**
 * Replanifie les trois rappels à partir de l'état courant. Appelé au démarrage (profil chargé)
 * et après chaque activité pertinente. No-op sur le web, tolérant aux erreurs.
 */
export async function syncNotifications({ profile, stats, progress, catalog }: SyncInput): Promise<void> {
  if (!notificationsAvailable()) return;
  try {
    const prefs = notificationPrefs(profile);
    if (!profile || isParentProfile(profile) || !prefs.enabled || !(await hasPermission())) {
      await cancelAll();
      return;
    }
    const now = new Date();
    await scheduleDailyReminder(prefs.hour, prefs.minute);
    await scheduleStreakReminder(stats, now);
    const reviewAt = nextOccurrence(REVIEW_REMINDER_HOUR, 0, now).getTime();
    const count = catalog ? countReviewsDueAt(catalog, progress, profile.year, reviewAt) : 0;
    await scheduleReviewReminder(count, now);
  } catch (e) {
    /* jamais bloquant */
  }
}
